import { Component, OnInit, ViewChild, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Table, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MenuItem, MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { TooltipModule } from 'primeng/tooltip';
import { LayoutService } from 'src/app/layout/service/layout.service';
import { PanelModule } from 'primeng/panel';
import { CheckboxModule } from 'primeng/checkbox';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';

import { GroupDTO, GroupUserDTO, UpdateGroupDTO } from '../../model/admin-dto';
import { GroupsService } from '../../services/groups.service';
import { AddUserComponent } from '../users/add-user/add-user.component';
import { KeycloakInfoService } from '../../services/keycloak-info.service';
import { PageTitleComponent } from '../../ui/page-title/page-title.component';

@Component({
  selector: 'rqa-groups',
  templateUrl: './groups.component.html',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    MenuModule,
    TranslateModule,
    TooltipModule,
    PanelModule,
    CheckboxModule,
    IconFieldModule,
    InputIconModule,
    PageTitleComponent
  ],
  providers: [GroupsService, DialogService],
  standalone: true
})
export class GroupsComponent implements OnInit {
  private readonly groupsService = inject(GroupsService);
  private readonly dialogService = inject(DialogService);
  private readonly translate = inject(TranslateService);
  private readonly layoutService = inject(LayoutService);
  private readonly router = inject(Router);
  private readonly keycloakInfoService = inject(KeycloakInfoService);

  @ViewChild('groupsTable') pTable!: Table;

  readonly groups = signal<GroupDTO[]>([]);
  readonly expandedGroups = signal<Set<string>>(new Set());
  readonly groupUsers = signal<Map<string, GroupUserDTO[]>>(new Map());
  readonly loading = signal<boolean>(false);

  readonly tableScrollHeight = computed(() => this.layoutService.calculateTableHeight());

  dialogRef: DynamicDialogRef | undefined;
  rowMenuItems: MenuItem[] = [];
  groupUserMenuItems: MenuItem[] = [];

  rolesSelectOptions = this.keycloakInfoService.roles;
  groupsSelectOptions = this.keycloakInfoService.groups;
  time = this.keycloakInfoService.time;
  locale = this.keycloakInfoService.locale;
  messageService = inject(MessageService);

  ngOnInit(): void {
    this.getGroups();
  }

  getGroups(): void {
    this.loading.set(true);
    this.groupsService.getGroupsList().subscribe({
      next: (response) => {
        this.groups.set(response);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
      }
    });
  }

  toggleGroupExpansion(group: GroupDTO): void {
    const expanded = this.expandedGroups();
    const newExpanded = new Set(expanded);

    if (expanded.has(group.id)) {
      newExpanded.delete(group.id);
    } else {
      newExpanded.add(group.id);
      this.loadGroupUsers(group.id);
    }

    this.expandedGroups.set(newExpanded);
  }

  isGroupExpanded(groupId: string): boolean {
    return this.expandedGroups().has(groupId);
  }

  loadGroupUsers(groupId: string): void {
    if (this.groupUsers().has(groupId)) {
      return;
    }
  }

  getGroupUsersArray(groupId: string): GroupUserDTO[] {
    return this.groupUsers().get(groupId) || [];
  }

  openEditGroup(group: GroupDTO): void {
    this.router.navigate(['/groups/edit', group.id]);
  }

  openDeleteGroup(group: GroupDTO): void {
    this.dialogRef = this.dialogService.open(ConfirmModalComponent, {
      header: this.translate.instant('groups.dialog.delete.title'),
      width: '450px',
      modal: true,
      data: {
        content: this.translate.instant('groups.dialog.delete.message', { groupName: group.name })
      }
    });

    this.dialogRef.onClose.subscribe((result) => {
      if (result) {
        this.groupsService.deleteGroup(group.id).subscribe({
          next: (response) => {
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('groups.dialog.delete.success')
            });
            this.getGroups();
          }
        });
      }
    });
  }

  openEditUser(user: GroupUserDTO): void {
    this.dialogRef = this.dialogService.open(AddUserComponent, {
      header: this.translate.instant('users.dialog.edit.title'),
      width: '75%',
      maximizable: true,
      modal: true,
      data: {
        editMode: true,
        user: user,
        locale: this.locale(),
        groups: this.groupsSelectOptions(),
        roles: this.rolesSelectOptions(),
        time: this.time()
      }
    });

    this.dialogRef.onClose.subscribe((result) => {
      if (result) {
        this.getGroups();
      }
    });
  }

  openRemoveUserFromGroup(group: GroupDTO, user: GroupUserDTO): void {
    this.dialogRef = this.dialogService.open(ConfirmModalComponent, {
      header: this.translate.instant('groups.dialog.removeUser.title'),
      width: '450px',
      modal: true,
      data: {
        content: this.translate.instant('groups.dialog.removeUser.message', {
          username: user.username
        })
      }
    });

    this.dialogRef.onClose.subscribe((result) => {
      if (result) {
        const updateGroupData: UpdateGroupDTO = {
          id: group.id,
          name: group.name,
          group: group.groupValue,
          users: group.webRqaUsers.filter((u) => u.username !== user.username).map((u) => u.username)
        };

        this.groupsService.updateGroup(updateGroupData).subscribe({
          next: (response) => {
            console.log('Group updated successfully:', response);
            this.getGroups();
          },
          error: (error) => {
            console.error('Error updating group:', error);
          }
        });
      }
    });
  }

  onShowGroupMenu(group: GroupDTO): void {
    this.rowMenuItems = [
      {
        label: this.translate.instant('groups.actions.edit'),
        icon: 'pi pi-pencil',
        command: () => this.openEditGroup(group)
      },
      {
        label: this.translate.instant('groups.actions.delete'),
        icon: 'pi pi-trash',
        command: () => this.openDeleteGroup(group)
      }
    ];
  }

  onShowUserMenu(group: GroupDTO, user: GroupUserDTO): void {
    this.groupUserMenuItems = [
      {
        label: this.translate.instant('groups.actions.editUser'),
        icon: 'pi pi-pencil',
        command: () => this.openEditUser(user)
      },
      {
        label: this.translate.instant('groups.actions.removeFromGroup'),
        icon: 'pi pi-user-minus',
        disabled: group.webRqaUsers.length === 1,
        command: () => this.openRemoveUserFromGroup(group, user)
      }
    ];
  }

  filterGroups(event: Event, matchType: string): void {
    this.pTable.filterGlobal((event.target as HTMLInputElement).value, matchType);
  }

  filterGroupUsers(event: Event, groupUsersTable: Table): void {
    groupUsersTable.filterGlobal((event.target as HTMLInputElement).value, 'contains');
  }

  refresh(): void {
    this.getGroups();
    this.expandedGroups.set(new Set());
    this.groupUsers.set(new Map());
  }

  openAddGroup(): void {
    this.router.navigate(['/groups/add']);
  }
}
